import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { type Tone, toneClasses } from "./tone";

const SIZE = {
  xs: "size-[22px] text-[11px] [&_svg]:size-[12px]",
  sm: "size-[30px] [&_svg]:size-[15px]",
  md: "size-[38px] [&_svg]:size-[19px]",
  /** The tile a drop zone stands on, with a radius of its own. */
  lg: "size-14 rounded-[14px] [&_svg]:size-[26px]",
} as const;

/** The grounds that are the tile's own rather than a tone's surface. */
const OWN_GROUND = {
  brand: "bg-muted text-primary",
  solid: "bg-brand-start text-white",
  done: "bg-success text-white",
  alert: "bg-destructive text-white",
  open: "border-[1.5px] border-border text-transparent",
} as const;

function hasOwnGround(
  tone: Tone | keyof typeof OWN_GROUND,
): tone is keyof typeof OWN_GROUND {
  return Object.hasOwn(OWN_GROUND, tone);
}

const SHAPE = {
  square: "rounded-control",
  round: "rounded-full",
} as const;

/**
 * One icon on a tinted tile, beside the words it goes with or standing for a
 * result on its own. `brand` is the product's quiet ground with the icon in
 * its violet; a tone is for a tile whose colour means something, as a tick
 * for a thing done.
 *
 * `numeral` holds a number in mono instead, for a numbered item such as a
 * section of a plan, on the `xs` tile.
 *
 * `solid` is the brand's own fill with white on it, for the number of a step
 * a page is built round. Its numeral is set heavy in the body face, as the
 * design draws a step's number.
 *
 * `alert` is the destructive colour filled with white, for a count of what
 * waits for the attorney, as the rail counts the changes Praxis suggested.
 *
 * `done` and `open` are the two states of a checklist item, as the locked
 * stage's criteria draw them: the done colour filled with a white mark once
 * the item is met, and an empty ring with its mark hidden until then, so a
 * row keeps its place as it is ticked.
 *
 * Hidden from a reader: the words beside it say what it shows.
 */
export function IconTile({
  tone = "brand",
  shape = "square",
  size = "sm",
  ...shown
}: {
  tone?: Tone | keyof typeof OWN_GROUND;
  shape?: keyof typeof SHAPE;
  size?: keyof typeof SIZE;
} & (
  | { children: ReactNode; numeral?: never }
  | { numeral: number; children?: never }
)) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "grid flex-none place-items-center",
        SHAPE[shape],
        SIZE[size],
        hasOwnGround(tone) ? OWN_GROUND[tone] : toneClasses(tone, "surface"),
      )}
    >
      {shown.numeral === undefined ? (
        shown.children
      ) : (
        <span
          className={
            tone === "solid" ? "text-[15px] font-extrabold" : "font-mono"
          }
        >
          {shown.numeral}
        </span>
      )}
    </span>
  );
}
